import { useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, RotateCcw } from "lucide-react";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { PlayProps } from "@/types/puzzle";

type ContainerId = "answer" | "bank";
type Containers = Record<ContainerId, string[]>;

// The resting chip face — reused by the drag overlay so the lifted tile matches.
const TILE_FACE =
  "rounded-xl border-2 border-b-4 border-border bg-card text-foreground px-4 py-2 text-sm font-bold";

// Pointer-based collision so a word can be dropped onto an EMPTY zone (closest-
// corner detection misses empty containers); fall back to corners for keyboard.
const collisionDetection: CollisionDetection = (args) => {
  const pointer = pointerWithin(args);
  return pointer.length > 0 ? pointer : closestCorners(args);
};

export default function ReorderWordsPlay({ data, onAnswer, result }: PlayProps) {
  const { t } = useTranslation();

  // Server delivers the tiles already shuffled. Give each a stable, unique id so
  // a repeated word (e.g. "the … the") doesn't collide as a drag id.
  const tiles = useMemo(
    () => ((data.words as string[]) ?? []).map((word, i) => ({ id: `w${i}`, word })),
    [data.words],
  );
  const wordOf = useMemo(() => new Map(tiles.map((tl) => [tl.id, tl.word])), [tiles]);

  const [containers, setContainers] = useState<Containers>(() => ({
    answer: [],
    bank: tiles.map((tl) => tl.id),
  }));
  const [activeId, setActiveId] = useState<string | null>(null);
  // A browser may fire a stray `click` right after a drag ends; swallow it so a
  // reorder-drag doesn't also bounce the tile to the other zone.
  const skipNextClickRef = useRef(false);

  const answered = result !== null;
  const isCorrect = result?.correct ?? false;
  const correctSentence = (result?.solution.correctSentence as string) ?? "";
  const allPlaced = containers.bank.length === 0 && containers.answer.length > 0;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Which container does an id live in? (`id` may be an item id, or a zone id.)
  const locate = (state: Containers, id: string): ContainerId | null =>
    id === "answer" || id === "bank"
      ? id
      : state.answer.includes(id)
        ? "answer"
        : state.bank.includes(id)
          ? "bank"
          : null;

  // Pull `aId` out of its current zone and splice it into `to` at the spot it's
  // hovering (or the end when hovering the empty zone itself).
  const moveAcross = (prev: Containers, aId: string, to: ContainerId, overId: string): Containers => {
    const from = locate(prev, aId);
    if (!from || from === to) return prev;
    const overIndex = prev[to].indexOf(overId);
    const insertAt = overIndex >= 0 ? overIndex : prev[to].length;
    return {
      ...prev,
      [from]: prev[from].filter((id) => id !== aId),
      [to]: [...prev[to].slice(0, insertAt), aId, ...prev[to].slice(insertAt)],
    };
  };

  // Tap a word to send it to the other zone (bank → end of answer, answer → bank).
  // Dragging is for fine-grained reordering; tapping is the quick path.
  const handleTileClick = (id: string) => {
    if (answered) return;
    if (skipNextClickRef.current) {
      skipNextClickRef.current = false;
      return;
    }
    setContainers((prev) => {
      const from = locate(prev, id);
      if (!from) return prev;
      const to: ContainerId = from === "bank" ? "answer" : "bank";
      return { ...prev, [from]: prev[from].filter((x) => x !== id), [to]: [...prev[to], id] };
    });
  };

  const handleDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const handleDragCancel = () => setActiveId(null);

  // After any real drag, ignore the click the browser may synthesize next.
  const suppressClickAfterDrag = () => {
    skipNextClickRef.current = true;
    setTimeout(() => {
      skipNextClickRef.current = false;
    }, 0);
  };

  // Reorder LIVE as the tile is dragged, so the placeholder is the dragged tile
  // itself, sitting exactly where it will drop (cross-zone via moveAcross, in-zone
  // via arrayMove). Settling happens here, so onDragEnd does no reordering.
  const handleDragOver = (e: DragOverEvent) => {
    const aId = String(e.active.id);
    const oId = e.over ? String(e.over.id) : null;
    if (!oId || oId === aId) return;
    setContainers((prev) => {
      const from = locate(prev, aId);
      const to = locate(prev, oId);
      if (!from || !to) return prev;
      if (from !== to) return moveAcross(prev, aId, to, oId);
      const items = prev[to];
      const oldIndex = items.indexOf(aId);
      const overIndex = oId === to ? items.length - 1 : items.indexOf(oId);
      if (oldIndex < 0 || overIndex < 0 || oldIndex === overIndex) return prev;
      return { ...prev, [to]: arrayMove(items, oldIndex, overIndex) };
    });
  };

  const handleDragEnd = () => {
    setActiveId(null);
    suppressClickAfterDrag();
  };

  const handleReset = () => setContainers({ answer: [], bank: tiles.map((tl) => tl.id) });

  const handleCheck = () => {
    if (!allPlaced || answered) return;
    onAnswer(
      containers.answer.flatMap((id) => {
        const w = wordOf.get(id);
        return w ? [w] : [];
      }),
    );
  };

  return (
    <div className="space-y-6 max-w-lg mx-auto">
      <h2 className="text-lg font-medium text-muted-foreground text-center animate-fade-in-up">
        {t("puzzleStudent.reorderWords.prompt")}
      </h2>

      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="rounded-2xl border-2 border-border p-4">
          {/* Answer line — tap/drag words up here and reorder them. Wraps to
              multiple lines as the sentence grows. */}
          <Zone
            id="answer"
            items={containers.answer}
            className={`flex flex-wrap content-start items-center gap-2 min-h-[3.5rem] border-b-2 pb-4 transition-colors ${
              answered ? (isCorrect ? "border-green-500" : "border-red-500") : "border-border"
            }`}
          >
            {containers.answer.map((id) => (
              <SortableWord
                key={id}
                id={id}
                word={wordOf.get(id) ?? ""}
                disabled={answered}
                onClick={() => handleTileClick(id)}
              />
            ))}
          </Zone>

          {/* Word bank */}
          <Zone
            id="bank"
            items={containers.bank}
            className="flex flex-wrap content-start items-center gap-2 min-h-[3.5rem] pt-4"
          >
            {containers.bank.map((id) => (
              <SortableWord
                key={id}
                id={id}
                word={wordOf.get(id) ?? ""}
                disabled={answered}
                onClick={() => handleTileClick(id)}
              />
            ))}
          </Zone>
        </div>

        <DragOverlay>
          {activeId ? <div className={`${TILE_FACE} cursor-grabbing shadow-lg`}>{wordOf.get(activeId)}</div> : null}
        </DragOverlay>
      </DndContext>

      {!answered && (
        <div className="flex gap-2 justify-center">
          {containers.answer.length > 0 && (
            <Button variant="ghost" size="sm" onClick={handleReset}>
              <RotateCcw className="w-4 h-4 mr-1" /> {t("puzzleStudent.reorderWords.reset")}
            </Button>
          )}
          <Button onClick={handleCheck} disabled={!allPlaced} className="px-8">
            {t("puzzleStudent.reorderWords.check")}
          </Button>
        </div>
      )}

      {answered && (
        <div
          role="status"
          aria-live="polite"
          className={`flex items-center gap-3 p-4 rounded-lg animate-fade-in-up ${
            isCorrect ? "bg-green-500/10 text-green-700" : "bg-red-500/10 text-red-700"
          }`}
        >
          {isCorrect ? (
            <CheckCircle className="w-5 h-5 shrink-0 animate-bounce-in" />
          ) : (
            <XCircle className="w-5 h-5 shrink-0 animate-shake" />
          )}
          <span className="text-sm font-semibold">
            {isCorrect
              ? t("puzzleStudent.reorderWords.correct")
              : t("puzzleStudent.reorderWords.correctOrder", { sentence: correctSentence })}
          </span>
        </div>
      )}
    </div>
  );
}

// A droppable zone wrapping a sortable list — so words can be dropped onto it
// even when it's empty.
function Zone({
  id,
  items,
  className,
  children,
}: {
  id: ContainerId;
  items: string[];
  className: string;
  children: ReactNode;
}) {
  const { setNodeRef } = useDroppable({ id });
  return (
    <SortableContext id={id} items={items} strategy={rectSortingStrategy}>
      <div ref={setNodeRef} className={className}>
        {children}
      </div>
    </SortableContext>
  );
}

function SortableWord({
  id,
  word,
  disabled,
  onClick,
}: {
  id: string;
  word: string;
  disabled: boolean;
  onClick: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style = { transform: CSS.Transform.toString(transform), transition };

  // While dragging, the tile becomes a solid muted "shadow" at the live drop spot
  // (the real word floats in the DragOverlay). It renders its own word, kept
  // transparent, so the shadow is the exact size of the word being moved.
  const look = isDragging
    ? "border-transparent bg-muted text-transparent"
    : `border-border bg-card text-foreground${disabled ? "" : " cursor-grab active:cursor-grabbing"}`;

  return (
    <button
      type="button"
      ref={setNodeRef}
      style={style}
      disabled={disabled}
      onClick={onClick}
      {...attributes}
      {...listeners}
      className={`rounded-xl border-2 border-b-4 px-4 py-2 text-sm font-bold touch-none select-none transition-colors ${look}`}
    >
      {word}
    </button>
  );
}
